import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  LinearProgress,
  CircularProgress,
  Button,
  useMediaQuery,
  IconButton,
  Fade,
  Zoom,
  Alert,
  Skeleton,
  Chip,
} from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import { useTheme, alpha } from '@mui/material/styles';
import MarkdownMessage from './MarkdownMessage';

// ============================================================================
// Accessibility: Screen Reader Announcer Component
// ============================================================================

/**
 * ScreenReaderAnnouncer - Announces messages to screen readers without visual display
 * Uses aria-live regions for dynamic content updates
 */
interface ScreenReaderAnnouncerProps {
  message: string;
  /** 'polite' waits for a pause, 'assertive' interrupts immediately */
  priority?: 'polite' | 'assertive';
}

const ScreenReaderAnnouncer = ({ message, priority = 'polite' }: ScreenReaderAnnouncerProps) => {
  return (
    <Box sx={visuallyHidden}>
      <div
        role="status"
        aria-live={priority}
        aria-atomic="true"
      >
        {message}
      </div>
    </Box>
  );
};
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import StarIcon from '@mui/icons-material/Star';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import CelebrationIcon from '@mui/icons-material/Celebration';
import SaveIcon from '@mui/icons-material/Save';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import HomeIcon from '@mui/icons-material/Home';
import { motion, useMotionValue, useTransform, PanInfo, AnimatePresence } from 'framer-motion';
import { usePreferencesGame, getGameProgress } from '../hooks/usePreferencesGame';
import { GameRound, StartGameRequest, PreferenceCategory, GameCompleteResponse } from '../types/preferencesGame';

// ============================================================================
// Image Preloading Utilities
// ============================================================================

/**
 * Preload an image and return a promise that resolves when loaded
 */
function preloadImage(url: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve();
    img.onerror = () => reject(new Error(`Failed to preload: ${url}`));
    img.src = url;
  });
}

/**
 * Extract image URLs from a game round
 */
function getImageUrlsFromRound(round: GameRound | null | undefined): string[] {
  if (!round || round.type !== 'image') return [];
  const urls: string[] = [];
  if (round.optionA.imageUrl) urls.push(round.optionA.imageUrl);
  if (round.optionB.imageUrl) urls.push(round.optionB.imageUrl);
  return urls;
}

/**
 * Custom hook to manage image preloading with a cache
 */
function useImagePreloader() {
  // Set of URLs that have been preloaded
  const preloadedCache = useRef<Set<string>>(new Set());
  // Set of URLs currently being preloaded
  const preloadingInProgress = useRef<Set<string>>(new Set());

  /**
   * Check if an image URL is already preloaded
   */
  const isPreloaded = useCallback((url: string): boolean => {
    return preloadedCache.current.has(url);
  }, []);

  /**
   * Preload images for a round (skips already preloaded images)
   */
  const preloadRound = useCallback((round: GameRound | null | undefined): void => {
    const urls = getImageUrlsFromRound(round);

    urls.forEach(url => {
      // Skip if already preloaded or currently loading
      if (preloadedCache.current.has(url) || preloadingInProgress.current.has(url)) {
        return;
      }

      // Mark as loading
      preloadingInProgress.current.add(url);

      // Preload the image
      preloadImage(url)
        .then(() => {
          preloadedCache.current.add(url);
          preloadingInProgress.current.delete(url);
        })
        .catch(() => {
          // Remove from in-progress on failure, allow retry later
          preloadingInProgress.current.delete(url);
        });
    });
  }, []);

  /**
   * Check if all images in a round are preloaded
   */
  const isRoundPreloaded = useCallback((round: GameRound | null | undefined): boolean => {
    const urls = getImageUrlsFromRound(round);
    return urls.every(url => preloadedCache.current.has(url));
  }, []);

  /**
   * Clear the preload cache (useful when restarting game)
   */
  const clearCache = useCallback(() => {
    preloadedCache.current.clear();
    preloadingInProgress.current.clear();
  }, []);

  return {
    isPreloaded,
    preloadRound,
    isRoundPreloaded,
    clearCache,
  };
}

// Constants for swipe gesture
const SWIPE_THRESHOLD = 100; // Minimum distance to trigger a selection
const SWIPE_VELOCITY_THRESHOLD = 500; // Velocity threshold for quick swipes

// ============================================================================
// Milestone Celebration Types and Component
// ============================================================================

/** Milestone thresholds for celebrations */
const MILESTONE_THRESHOLDS = [25, 50, 75, 100] as const;
type MilestoneValue = typeof MILESTONE_THRESHOLDS[number];

interface MilestoneInfo {
  percentage: MilestoneValue;
  icon: React.ReactNode;
  message: string;
  color: string;
}

/** Get milestone information based on percentage */
function getMilestoneInfo(percentage: MilestoneValue): MilestoneInfo {
  switch (percentage) {
    case 25:
      return {
        percentage: 25,
        icon: <StarIcon sx={{ fontSize: 48, color: '#FFD700' }} />,
        message: 'Great start!',
        color: '#FFD700',
      };
    case 50:
      return {
        percentage: 50,
        icon: <StarIcon sx={{ fontSize: 56, color: '#FFA500' }} />,
        message: 'Halfway there!',
        color: '#FFA500',
      };
    case 75:
      return {
        percentage: 75,
        icon: <EmojiEventsIcon sx={{ fontSize: 64, color: '#C0C0C0' }} />,
        message: 'Almost done!',
        color: '#C0C0C0',
      };
    case 100:
      return {
        percentage: 100,
        icon: <CelebrationIcon sx={{ fontSize: 72, color: '#FFD700' }} />,
        message: 'Complete!',
        color: '#FFD700',
      };
  }
}

interface MilestoneCelebrationProps {
  milestone: MilestoneValue;
  onComplete: () => void;
}

/**
 * MilestoneCelebration - Animated celebration overlay for progress milestones
 */
const MilestoneCelebration = ({ milestone, onComplete }: MilestoneCelebrationProps) => {
  const theme = useTheme();
  const info = getMilestoneInfo(milestone);
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');

  // Auto-dismiss after animation
  useEffect(() => {
    const timer = setTimeout(onComplete, prefersReducedMotion ? 1000 : 1800);
    return () => clearTimeout(timer);
  }, [onComplete, prefersReducedMotion]);

  // Accessible message for screen readers
  const accessibleMessage = `${milestone}% complete! ${info.message}`;

  // Generate confetti particles for 100% milestone
  const confettiParticles = useMemo(() => {
    if (milestone !== 100 || prefersReducedMotion) return [];
    return Array.from({ length: 20 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      delay: Math.random() * 0.5,
      color: ['#FFD700', '#FF6B6B', '#4CAF50', '#2196F3', '#9C27B0'][i % 5],
      rotation: Math.random() * 360,
      size: 8 + Math.random() * 8,
    }));
  }, [milestone, prefersReducedMotion]);

  // Animation variants for reduced motion
  const backdropVariants = {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 },
  };

  const iconVariants = prefersReducedMotion
    ? {
        initial: { opacity: 0 },
        animate: { opacity: 1, transition: { duration: 0.2 } },
        exit: { opacity: 0, transition: { duration: 0.2 } },
      }
    : {
        initial: { scale: 0, rotate: -180, opacity: 0 },
        animate: {
          scale: [0, 1.3, 1],
          rotate: [-180, 15, 0],
          opacity: 1,
          transition: {
            duration: 0.6,
            ease: 'easeOut' as const,
            times: [0, 0.6, 1],
          },
        },
        exit: { scale: 0, opacity: 0, transition: { duration: 0.3 } },
      };

  const textVariants = prefersReducedMotion
    ? {
        initial: { opacity: 0 },
        animate: { opacity: 1, transition: { delay: 0.2, duration: 0.2 } },
        exit: { opacity: 0 },
      }
    : {
        initial: { y: 20, opacity: 0 },
        animate: {
          y: 0,
          opacity: 1,
          transition: { delay: 0.4, duration: 0.3 },
        },
        exit: { y: -20, opacity: 0 },
      };

  const percentVariants = prefersReducedMotion
    ? {
        initial: { opacity: 0 },
        animate: { opacity: 1, transition: { delay: 0.3, duration: 0.2 } },
        exit: { opacity: 0 },
      }
    : {
        initial: { scale: 0.5, opacity: 0 },
        animate: {
          scale: 1,
          opacity: 1,
          transition: { delay: 0.5, duration: 0.3, type: 'spring' as const, stiffness: 200 },
        },
        exit: { scale: 0, opacity: 0 },
      };

  // Sparkle animation for stars (undefined for reduced motion to skip animation)
  const sparkleVariants = prefersReducedMotion
    ? undefined
    : {
        animate: {
          scale: [1, 1.2, 1],
          rotate: [0, 5, -5, 0],
          transition: {
            duration: 1,
            repeat: Infinity,
            ease: 'easeInOut' as const,
          },
        },
      };

  return (
    <AnimatePresence>
      <motion.div
        variants={backdropVariants}
        initial="initial"
        animate="animate"
        exit="exit"
        transition={{ duration: 0.3 }}
        role="alert"
        aria-label={accessibleMessage}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: alpha(theme.palette.background.default, 0.92),
          zIndex: 100,
          overflow: 'hidden',
        }}
      >
        {/* Screen reader announcement */}
        <Box sx={visuallyHidden}>
          <div role="status" aria-live="assertive">{accessibleMessage}</div>
        </Box>

        {/* Confetti particles for 100% milestone */}
        {confettiParticles.map((particle) => (
          <motion.div
            key={particle.id}
            initial={{ y: -20, x: `${particle.x}vw`, opacity: 1, rotate: 0 }}
            animate={{
              y: '100vh',
              rotate: particle.rotation + 360,
              opacity: [1, 1, 0],
            }}
            transition={{
              duration: 2.5,
              delay: particle.delay,
              ease: 'easeIn',
            }}
            style={{
              position: 'absolute',
              top: 0,
              width: particle.size,
              height: particle.size,
              backgroundColor: particle.color,
              borderRadius: particle.id % 3 === 0 ? '50%' : '2px',
            }}
          />
        ))}

        {/* Radial glow effect */}
        {!prefersReducedMotion && (
          <motion.div
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: [0, 2, 2.5], opacity: [0, 0.3, 0] }}
            transition={{ duration: 1, ease: 'easeOut' }}
            style={{
              position: 'absolute',
              width: 200,
              height: 200,
              borderRadius: '50%',
              background: `radial-gradient(circle, ${alpha(info.color, 0.4)} 0%, transparent 70%)`,
            }}
          />
        )}

        {/* Icon with sparkle animation */}
        <motion.div
          variants={iconVariants}
          initial="initial"
          animate="animate"
          exit="exit"
        >
          {sparkleVariants ? (
            <motion.div variants={sparkleVariants} animate="animate">
              {info.icon}
            </motion.div>
          ) : (
            info.icon
          )}
        </motion.div>

        {/* Percentage badge */}
        <motion.div
          variants={percentVariants}
          initial="initial"
          animate="animate"
          exit="exit"
        >
          <Box
            sx={{
              mt: 2,
              px: 3,
              py: 1,
              borderRadius: 4,
              bgcolor: alpha(info.color, 0.15),
              border: `2px solid ${info.color}`,
            }}
          >
            <Typography
              variant="h4"
              sx={{
                fontWeight: 'bold',
                color: info.color,
                textShadow: `0 0 10px ${alpha(info.color, 0.5)}`,
              }}
            >
              {milestone}%
            </Typography>
          </Box>
        </motion.div>

        {/* Message text */}
        <motion.div
          variants={textVariants}
          initial="initial"
          animate="animate"
          exit="exit"
        >
          <Typography
            variant="h5"
            sx={{
              mt: 2,
              fontWeight: 600,
              color: 'text.primary',
            }}
          >
            {info.message}
          </Typography>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

/**
 * Hook to track progress milestones and trigger celebrations
 */
function useMilestoneTracker(currentProgress: number, isActive: boolean) {
  // Track which milestones have been celebrated in this game session
  const celebratedMilestones = useRef<Set<MilestoneValue>>(new Set());
  const [celebratingMilestone, setCelebratingMilestone] = useState<MilestoneValue | null>(null);
  const previousProgress = useRef<number>(0);

  // Reset celebrated milestones when game becomes inactive (new game)
  useEffect(() => {
    if (!isActive) {
      celebratedMilestones.current.clear();
      previousProgress.current = 0;
    }
  }, [isActive]);

  // Check for milestone crossing when progress changes
  useEffect(() => {
    if (!isActive || celebratingMilestone) return;

    // Find if we've crossed a new milestone
    for (const threshold of MILESTONE_THRESHOLDS) {
      // Check if we just crossed this threshold (weren't at or past it before, but are now)
      const wasBelowThreshold = previousProgress.current < threshold;
      const isAtOrAboveThreshold = currentProgress >= threshold;
      const notYetCelebrated = !celebratedMilestones.current.has(threshold);

      if (wasBelowThreshold && isAtOrAboveThreshold && notYetCelebrated) {
        celebratedMilestones.current.add(threshold);
        setCelebratingMilestone(threshold);
        break; // Only celebrate one milestone at a time
      }
    }

    previousProgress.current = currentProgress;
  }, [currentProgress, isActive, celebratingMilestone]);

  const dismissCelebration = useCallback(() => {
    setCelebratingMilestone(null);
  }, []);

  return {
    celebratingMilestone,
    dismissCelebration,
    resetMilestones: useCallback(() => {
      celebratedMilestones.current.clear();
      previousProgress.current = 0;
    }, []),
  };
}

interface ThisOrThatGameProps {
  /** Called when the game is completed with the final preferences text (after user saves to profile) */
  onComplete?: (preferences: string) => void;
  /** Called when the user abandons the game */
  onAbandon?: () => void;
  /** Called when the user closes the game (via X button or Discard) */
  onClose?: () => void;
  /** Initial game options */
  gameOptions?: StartGameRequest;
  /** Whether to show the close button */
  showCloseButton?: boolean;
}

/**
 * ThisOrThatGame - A split-screen preference selection game
 *
 * Presents users with binary choices (images or text) to determine
 * home preferences in an engaging, visual way.
 */
const ThisOrThatGame = ({
  onComplete,
  onAbandon,
  onClose,
  gameOptions,
  showCloseButton = true,
}: ThisOrThatGameProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const {
    state,
    isLoading,
    error,
    startGame,
    submitChoice,
    abandonGame,
    refreshState,
    clearError,
    setNextRoundPreloadCallback,
  } = usePreferencesGame();

  // Image preloading
  const { isPreloaded, preloadRound, clearCache } = useImagePreloader();

  // Track which option is being hovered (for keyboard focus indication)
  const [focusedOption, setFocusedOption] = useState<'A' | 'B' | null>(null);

  // Calculate progress early so we can use it for milestone tracking
  const progress = getGameProgress(state);

  // Track milestones and celebrations
  const { celebratingMilestone, dismissCelebration, resetMilestones } = useMilestoneTracker(
    progress,
    state.isActive
  );

  // Register preload callback with the hook to start preloading as soon as API responds
  useEffect(() => {
    setNextRoundPreloadCallback((nextRound) => {
      if (nextRound) {
        preloadRound(nextRound);
      }
    });

    // Cleanup on unmount
    return () => {
      setNextRoundPreloadCallback(null);
    };
  }, [setNextRoundPreloadCallback, preloadRound]);

  // Local state for animations
  const [selectedOption, setSelectedOption] = useState<'A' | 'B' | null>(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [imagesLoaded, setImagesLoaded] = useState({ A: false, B: false });

  // State for swipe direction indicator
  const [swipeDirection, setSwipeDirection] = useState<'left' | 'right' | null>(null);

  // State for exit animation direction (preserved after selection)
  const [exitDirection, setExitDirection] = useState<'left' | 'right'>('left');

  // Combined transition state to ensure atomic updates
  // - isExiting: controls exit animation
  // - displayRound: the round currently shown (independent of API state)
  // - pendingRound: queued round from API during exit animation
  const [transitionState, setTransitionState] = useState<{
    isExiting: boolean;
    displayRound: GameRound | null;
    pendingRound: GameRound | null;
  }>({
    isExiting: false,
    displayRound: null,
    pendingRound: null,
  });

  // Destructure for convenience
  const { isExiting, displayRound } = transitionState;

  // Debounce ref to prevent duplicate submissions
  const isSubmittingRef = useRef(false);

  // Completion screen state
  const [completionData, setCompletionData] = useState<{
    preferences: string | null;
    summary: {
      totalRounds: number;
      categoriesCovered: PreferenceCategory[];
      strongPreferences: string[];
    } | null;
    isSaving: boolean;
    hasSaved: boolean;
  }>({
    preferences: null,
    summary: null,
    isSaving: false,
    hasSaved: false,
  });

  // Check for reduced motion preference
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');

  // Accessibility: Screen reader announcements
  const [screenReaderMessage, setScreenReaderMessage] = useState('');

  // Ref for the game container to manage focus
  const gameContainerRef = useRef<HTMLDivElement>(null);

  // Motion values for swipe gesture (only used on mobile)
  const x = useMotionValue(0);

  // Transform motion value to visual indicators
  const leftIndicatorOpacity = useTransform(x, [-SWIPE_THRESHOLD, 0], [1, 0]);
  const rightIndicatorOpacity = useTransform(x, [0, SWIPE_THRESHOLD], [0, 1]);
  const cardRotation = useTransform(x, [-200, 0, 200], [-10, 0, 10]);
  const cardScale = useTransform(x, [-200, 0, 200], [0.95, 1, 0.95]);

  // Track initialization state to prevent 409 loop
  // 'pending' = not started, 'checking' = calling refreshState, 'ready' = can start new game if needed
  const [initState, setInitState] = useState<'pending' | 'checking' | 'ready'>('pending');

  // Guard to prevent duplicate startGame calls
  const isStartingRef = useRef(false);

  // Use displayRound for rendering, which can be updated independently of API state
  const currentRound = displayRound || state.currentRound;
  const displayRoundId = currentRound?.id;

  // Track whether we're in the middle of a selection animation (from click to exit complete)
  const isAnimatingSelectionRef = useRef(false);

  // Sync displayRound with state.currentRound
  // Key insight: When a new round arrives from API, we should ALWAYS queue it as pending
  // if we're in the middle of an animation. The pending round will be shown after exit completes.
  useEffect(() => {
    if (!state.currentRound) return;

    setTransitionState(prev => {
      const isNewRound = state.currentRound!.id !== prev.displayRound?.id;

      if (isNewRound) {
        if (isAnimatingSelectionRef.current || prev.isExiting) {
          // We're still animating - queue the new round
          return {
            ...prev,
            pendingRound: state.currentRound,
          };
        } else {
          // Not animating, show immediately
          return {
            isExiting: false,
            displayRound: state.currentRound,
            pendingRound: null,
          };
        }
      } else if (!prev.isExiting) {
        // Same round, not exiting - just sync
        return {
          ...prev,
          displayRound: state.currentRound,
        };
      } else {
        // Same round, exiting - no change needed
        return prev;
      }
    });
  }, [state.currentRound]);

  // When exit animation completes, show the pending round
  useEffect(() => {
    if (isExiting && transitionState.pendingRound) {
      // Exit animation is running and we have a pending round
      // Set a short timeout to let the exit animation complete, then show new round
      const timer = setTimeout(() => {
        isAnimatingSelectionRef.current = false;
        setTransitionState({
          isExiting: false,
          displayRound: transitionState.pendingRound,
          pendingRound: null,
        });
      }, 400); // Match the exit animation duration
      return () => clearTimeout(timer);
    }
  }, [isExiting, transitionState.pendingRound]);

  // If isExiting becomes true but there's no pending round yet, wait for it
  // This handles the case where exit starts before API returns
  useEffect(() => {
    if (isExiting && !transitionState.pendingRound && state.currentRound && state.currentRound.id !== displayRound?.id) {
      // We're exiting but API already returned with new round - show it after animation
      const timer = setTimeout(() => {
        isAnimatingSelectionRef.current = false;
        setTransitionState({
          isExiting: false,
          displayRound: state.currentRound,
          pendingRound: null,
        });
      }, 400);
      return () => clearTimeout(timer);
    }
  }, [isExiting, transitionState.pendingRound, state.currentRound, displayRound?.id]);

  // Reset animation state when display round changes
  useEffect(() => {
    setSelectedOption(null);
    setIsAnimating(false);
    setShowResult(false);
    setSwipeDirection(null);
    setFocusedOption(null);
    x.set(0);

    // Check if images are already preloaded for this round
    if (currentRound && currentRound.type === 'image') {
      const aPreloaded = currentRound.optionA.imageUrl ? isPreloaded(currentRound.optionA.imageUrl) : true;
      const bPreloaded = currentRound.optionB.imageUrl ? isPreloaded(currentRound.optionB.imageUrl) : true;
      setImagesLoaded({ A: aPreloaded, B: bPreloaded });
    } else {
      // For text rounds, mark as loaded
      setImagesLoaded({ A: true, B: true });
    }

    // Announce new round to screen readers
    if (currentRound) {
      setScreenReaderMessage(
        `Question ${state.roundNumber} of ${state.totalRounds}: ${currentRound.prompt}. ` +
        `Option 1: ${currentRound.optionA.label}. Option 2: ${currentRound.optionB.label}. ` +
        `Use arrow keys or numbers 1 and 2 to select.`
      );
    }
  }, [displayRoundId, x, currentRound, isPreloaded, state.roundNumber, state.totalRounds]);

  // Preload current round images when round changes
  useEffect(() => {
    if (state.currentRound) {
      preloadRound(state.currentRound);
    }
  }, [state.currentRound, preloadRound]);

  // Auto-start game if not active - first check for existing session
  // If an existing session is found, refreshState will populate it.
  // If no session exists, we start a new game.
  useEffect(() => {
    const initializeGame = async () => {
      // Only run once when pending
      if (initState !== 'pending') return;

      // If we already have an active game from props/context, nothing to do
      if (state.isActive || state.isComplete) {
        setInitState('ready');
        return;
      }

      // Prevent duplicate calls
      if (isStartingRef.current) return;
      isStartingRef.current = true;

      setInitState('checking');

      // Check if there's an existing session on the server
      // refreshState returns true if an active session was found
      const hasExistingSession = await refreshState();

      if (hasExistingSession) {
        // Existing session loaded, we're done
        setInitState('ready');
        isStartingRef.current = false;
        return;
      }

      // No existing session, start a new game
      await startGame(gameOptions);
      setInitState('ready');
      isStartingRef.current = false;
    };

    initializeGame();
  }, [initState, state.isActive, state.isComplete, refreshState, startGame, gameOptions]);

  // Detect game completion from state update (after submitChoice sets isComplete)
  // Automatically generate preferences preview when game completes
  useEffect(() => {
    const generatePreferencesPreview = async () => {
      if (state.isComplete && !showResult && !completionData.preferences) {
        setShowResult(true);
        // Announce completion to screen readers
        setScreenReaderMessage('Game complete! Your preference summary is being generated.');
        // Generate preferences without saving (preview mode)
        try {
          const response = await fetch('/api/account/preferences-game/complete', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'same-origin',
            body: JSON.stringify({
              sessionId: state.sessionId,
              updatePreferences: false, // Don't save yet, just preview
            }),
          });

          if (response.ok) {
            const data: GameCompleteResponse = await response.json();
            if (data.success && data.preferences) {
              setCompletionData(prev => ({
                ...prev,
                preferences: data.preferences || null,
                summary: data.summary || null,
              }));
            }
          }
        } catch (error) {
          console.error('Error generating preferences preview:', error);
        }
      }
    };

    generatePreferencesPreview();
  }, [state.isComplete, state.sessionId, showResult, completionData.preferences]);

  // Handle option selection
  const handleSelect = useCallback(
    (choice: 'A' | 'B') => {
      // Debounce: prevent duplicate submissions
      if (isSubmittingRef.current) return;
      if (isLoading || isAnimating || selectedOption || isExiting) return;

      // Set debounce flag immediately
      isSubmittingRef.current = true;
      // Mark that we're in selection animation (will be cleared when exit animation completes)
      isAnimatingSelectionRef.current = true;

      const selectedLabel = choice === 'A' ? currentRound?.optionA.label : currentRound?.optionB.label;

      // Set exit direction based on choice (A = left, B = right)
      setExitDirection(choice === 'A' ? 'left' : 'right');
      setSelectedOption(choice);
      setIsAnimating(true);

      // Announce selection to screen readers
      setScreenReaderMessage(`Selected: ${selectedLabel || choice}. Loading next question.`);

      // Fire API call immediately (don't await - let it run in background)
      submitChoice(choice).finally(() => {
        // Reset debounce flag after API completes
        setTimeout(() => {
          isSubmittingRef.current = false;
        }, 100);
      });

      // Trigger exit animation after brief highlight display (500ms total)
      setTimeout(() => {
        setTransitionState(prev => ({ ...prev, isExiting: true }));
        setIsAnimating(false);
      }, 500);
    },
    [isLoading, isAnimating, selectedOption, isExiting, submitChoice, currentRound]
  );

  // Keyboard navigation for desktop users
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Don't handle if game is not active or animating
      if (!state.isActive || isLoading || isAnimating || selectedOption || state.isComplete) {
        return;
      }

      // Don't handle if user is typing in an input field
      if (
        document.activeElement instanceof HTMLInputElement ||
        document.activeElement instanceof HTMLTextAreaElement
      ) {
        return;
      }

      switch (event.key) {
        case 'ArrowLeft':
          event.preventDefault();
          setFocusedOption('A');
          // Small delay to show focus before selecting
          setTimeout(() => handleSelect('A'), 150);
          break;
        case 'ArrowRight':
          event.preventDefault();
          setFocusedOption('B');
          // Small delay to show focus before selecting
          setTimeout(() => handleSelect('B'), 150);
          break;
        case '1':
        case 'a':
        case 'A':
          event.preventDefault();
          setFocusedOption('A');
          setTimeout(() => handleSelect('A'), 150);
          break;
        case '2':
        case 'b':
        case 'B':
          event.preventDefault();
          setFocusedOption('B');
          setTimeout(() => handleSelect('B'), 150);
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [state.isActive, state.isComplete, isLoading, isAnimating, selectedOption, handleSelect]);

  // Handle saving preferences to profile
  const handleSaveToProfile = useCallback(async () => {
    if (!state.sessionId || completionData.isSaving || completionData.hasSaved) return;

    setCompletionData(prev => ({ ...prev, isSaving: true }));

    try {
      // Call the API to save preferences to the user's profile
      const response = await fetch('/api/account/preferences-game/complete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({
          sessionId: state.sessionId,
          updatePreferences: true, // Actually save to profile
        }),
      });

      if (response.ok) {
        const data: GameCompleteResponse = await response.json();
        if (data.success) {
          setCompletionData(prev => ({
            ...prev,
            isSaving: false,
            hasSaved: true,
          }));
          // Notify parent component
          if (onComplete && completionData.preferences) {
            onComplete(completionData.preferences);
          }
        } else {
          setCompletionData(prev => ({ ...prev, isSaving: false }));
        }
      } else {
        setCompletionData(prev => ({ ...prev, isSaving: false }));
      }
    } catch (error) {
      console.error('Error saving preferences:', error);
      setCompletionData(prev => ({ ...prev, isSaving: false }));
    }
  }, [state.sessionId, completionData.isSaving, completionData.hasSaved, completionData.preferences, onComplete]);

  // Handle game abandonment
  const handleAbandon = useCallback(async () => {
    await abandonGame();
    onAbandon?.();
  }, [abandonGame, onAbandon]);

  // Handle restart (Play Again without saving)
  const handleRestart = useCallback(async () => {
    // Guard against duplicate calls
    if (isStartingRef.current) return;

    await abandonGame();
    setShowResult(false);
    setCompletionData({
      preferences: null,
      summary: null,
      isSaving: false,
      hasSaved: false,
    });
    clearCache(); // Clear preloaded images cache for fresh game
    resetMilestones(); // Reset milestone celebrations for new game

    // Use guard to prevent effect from also calling startGame
    isStartingRef.current = true;
    await startGame(gameOptions);
    isStartingRef.current = false;
  }, [abandonGame, startGame, gameOptions, clearCache, resetMilestones]);

  // Handle image load
  const handleImageLoad = useCallback((option: 'A' | 'B') => {
    setImagesLoaded((prev) => ({ ...prev, [option]: true }));
  }, []);

  // Handle drag/swipe for mobile
  const handleDrag = useCallback(
    (_event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
      // Update swipe direction indicator based on current position
      if (info.offset.x < -30) {
        setSwipeDirection('left');
      } else if (info.offset.x > 30) {
        setSwipeDirection('right');
      } else {
        setSwipeDirection(null);
      }
    },
    []
  );

  const handleDragEnd = useCallback(
    async (_event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
      setSwipeDirection(null);

      if (isLoading || isAnimating || selectedOption) return;

      const { offset, velocity } = info;

      // Check if swipe exceeds threshold (by distance or velocity)
      const swipedLeft =
        offset.x < -SWIPE_THRESHOLD || (offset.x < -50 && velocity.x < -SWIPE_VELOCITY_THRESHOLD);
      const swipedRight =
        offset.x > SWIPE_THRESHOLD || (offset.x > 50 && velocity.x > SWIPE_VELOCITY_THRESHOLD);

      if (swipedLeft) {
        // Swiped left = select Option A (shown on left in desktop, top on mobile)
        handleSelect('A');
      } else if (swipedRight) {
        // Swiped right = select Option B (shown on right in desktop, bottom on mobile)
        handleSelect('B');
      }

      // Reset position if not selected
      x.set(0);
    },
    [isLoading, isAnimating, selectedOption, handleSelect, x]
  );

  // Memoize motion variants for reduced motion
  const swipeCardVariants = useMemo(
    () => ({
      // Incoming card starts from behind (scaled down, slightly transparent)
      enter: prefersReducedMotion
        ? { opacity: 0 }
        : {
            scale: 0.92,
            opacity: 0,
            y: 10,
          },
      // Active card in center position
      center: prefersReducedMotion
        ? { opacity: 1 }
        : {
            scale: 1,
            opacity: 1,
            y: 0,
            x: 0,
            rotate: 0,
          },
      // Exit animation - card flies out in chosen direction
      exit: (direction: 'left' | 'right') =>
        prefersReducedMotion
          ? { opacity: 0 }
          : {
              x: direction === 'left' ? -400 : 400,
              opacity: 0,
              scale: 0.8,
              rotate: direction === 'left' ? -20 : 20,
              transition: {
                duration: 0.4,
                ease: 'easeInOut' as const,
              },
            },
    }),
    [prefersReducedMotion]
  );

  // Variants for the "next card" shadow behind the current card
  const nextCardVariants = useMemo(
    () => ({
      hidden: prefersReducedMotion
        ? { opacity: 0 }
        : {
            scale: 0.88,
            opacity: 0,
            y: 15,
          },
      visible: prefersReducedMotion
        ? { opacity: 0.5 }
        : {
            scale: 0.92,
            opacity: 0.6,
            y: 8,
          },
    }),
    [prefersReducedMotion]
  );

  // Render loading state
  if (isLoading && !state.currentRound) {
    return (
      <Box
        role="status"
        aria-label="Loading game"
        aria-busy="true"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: isMobile ? '100dvh' : 400,
          height: isMobile ? '100dvh' : 400,
          p: 3,
          boxSizing: 'border-box',
        }}
      >
        <CircularProgress size={48} aria-hidden="true" />
        <Typography variant="body1" sx={{ mt: 2 }}>
          Loading your game...
        </Typography>
      </Box>
    );
  }

  // Render error state
  if (error && !state.currentRound) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: isMobile ? '100dvh' : 400,
          height: isMobile ? '100dvh' : 400,
          p: 3,
          boxSizing: 'border-box',
        }}
      >
        <Alert
          severity="error"
          role="alert"
          action={
            <Button color="inherit" size="small" onClick={clearError} aria-label="Dismiss error and retry">
              Retry
            </Button>
          }
          sx={{ mb: 2 }}
        >
          {error}
        </Alert>
        <Button variant="contained" onClick={() => startGame(gameOptions)} aria-label="Try starting the game again">
          Try Again
        </Button>
      </Box>
    );
  }

  // Render completion state with preference summary
  if (state.isComplete || showResult) {
    const isLoadingPreferences = !completionData.preferences && !error;
    const categoriesCovered = completionData.summary?.categoriesCovered || [];
    const strongPreferences = completionData.summary?.strongPreferences || [];

    // Helper to format category for display
    const formatCategory = (category: string): string => {
      return category
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (char) => char.toUpperCase());
    };

    return (
      <Fade in timeout={500}>
        <Box
          role="region"
          aria-label="Game completion summary"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100dvh',
            overflow: 'hidden',
            boxSizing: 'border-box',
          }}
        >
          {/* Header */}
          <Box
            component="header"
            sx={{
              p: { xs: 2.5, sm: 3 },
              textAlign: 'center',
              borderBottom: 1,
              borderColor: 'divider',
              bgcolor: alpha(theme.palette.primary.main, 0.04),
              position: 'relative',
              flexShrink: 0,
            }}
          >
            {/* Close button */}
            {onClose && (
              <IconButton
                onClick={onClose}
                size="small"
                sx={{ position: 'absolute', top: 8, right: 8 }}
                aria-label="Close and return to profile"
              >
                <CloseIcon aria-hidden="true" />
              </IconButton>
            )}
            <Zoom in timeout={300}>
              <Box
                aria-hidden="true"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 56,
                  height: 56,
                  borderRadius: '50%',
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  mb: 1.5,
                }}
              >
                {completionData.hasSaved ? (
                  <CheckCircleIcon sx={{ fontSize: 32, color: 'primary.main' }} />
                ) : (
                  <HomeIcon sx={{ fontSize: 32, color: 'primary.main' }} />
                )}
              </Box>
            </Zoom>

            <Typography variant="h5" component="h1" fontWeight={600} gutterBottom>
              {completionData.hasSaved ? 'Preferences Saved!' : 'Your Home Preferences'}
            </Typography>

            <Typography variant="body2" color="text.secondary">
              {completionData.hasSaved
                ? 'Your preferences have been saved to your profile.'
                : `Based on your ${state.totalRounds} choices, here's what we learned about your ideal home.`}
            </Typography>

            {/* Categories covered chips */}
            {categoriesCovered.length > 0 && (
              <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 0.75, justifyContent: 'center' }}>
                {categoriesCovered.map((category) => (
                  <Chip
                    key={category}
                    label={formatCategory(category)}
                    size="small"
                    sx={{
                      fontSize: '0.75rem',
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      color: theme.palette.primary.main,
                      fontWeight: 500,
                    }}
                  />
                ))}
              </Box>
            )}
          </Box>

          {/* Preferences Summary Content */}
          <Box
            sx={{
              flex: 1,
              overflow: 'auto',
              p: { xs: 2, sm: 4 },
            }}
          >
            <Box sx={{ maxWidth: 720, mx: 'auto' }}>
              {isLoadingPreferences ? (
                <Box role="status" aria-label="Analyzing your choices" sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
                  <CircularProgress size={40} sx={{ mb: 2 }} aria-hidden="true" />
                  <Typography variant="body2" color="text.secondary">
                    Analyzing your choices...
                  </Typography>
                </Box>
              ) : completionData.preferences ? (
                <Box sx={{ mb: 3 }}>
                  <MarkdownMessage content={completionData.preferences} />
                </Box>
              ) : error ? (
                <Alert severity="error" sx={{ mb: 3 }}>
                  {error}
                </Alert>
              ) : null}

              {/* Strong Preferences Highlights */}
              {strongPreferences.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ fontWeight: 600 }}>
                    Key Insights
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {strongPreferences.slice(0, 5).map((pref, index) => (
                      <Box
                        key={index}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          p: 1.5,
                          borderRadius: 1.5,
                          bgcolor: alpha(theme.palette.primary.main, 0.08),
                          border: 1,
                          borderColor: alpha(theme.palette.primary.main, 0.15),
                        }}
                      >
                        <StarIcon sx={{ fontSize: 18, color: 'warning.main', flexShrink: 0 }} />
                        <Typography variant="body2">{pref}</Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          </Box>

          {/* Action Buttons */}
          <Box
            component="footer"
            role="group"
            aria-label="Game completion actions"
            sx={{
              p: 3,
              borderTop: 1,
              borderColor: 'divider',
              bgcolor: 'background.paper',
              flexShrink: 0,
            }}
          >
            {completionData.hasSaved ? (
              // Already saved - just show Play Again option
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleRestart}
                  startIcon={<RestartAltIcon aria-hidden="true" />}
                  fullWidth={isMobile}
                  aria-label="Play the preferences game again"
                >
                  Play Again
                </Button>
                {onClose && (
                  <Button
                    variant="outlined"
                    size="large"
                    onClick={onClose}
                    fullWidth={isMobile}
                    aria-label="Close the game and return to profile"
                  >
                    Done
                  </Button>
                )}
              </Box>
            ) : (
              // Not yet saved - show Save as primary CTA, secondary actions below
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, alignItems: 'center' }}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleSaveToProfile}
                  disabled={isLoadingPreferences || completionData.isSaving}
                  startIcon={
                    completionData.isSaving ? (
                      <CircularProgress size={20} color="inherit" aria-hidden="true" />
                    ) : (
                      <SaveIcon aria-hidden="true" />
                    )
                  }
                  fullWidth
                  sx={{ py: 1.5 }}
                  aria-label={completionData.isSaving ? 'Saving preferences to your profile' : 'Save these preferences to your profile'}
                  aria-busy={completionData.isSaving}
                >
                  {completionData.isSaving ? 'Saving...' : 'Save to Profile'}
                </Button>

                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                  <Button
                    variant="text"
                    size="small"
                    onClick={handleRestart}
                    startIcon={<RestartAltIcon aria-hidden="true" />}
                    disabled={completionData.isSaving}
                    color="inherit"
                    sx={{ color: 'text.secondary' }}
                    aria-label="Play again without saving these preferences"
                  >
                    Play Again
                  </Button>
                  {onClose && (
                    <Button
                      variant="text"
                      size="small"
                      onClick={() => {
                        // Discard without saving
                        abandonGame();
                        onClose();
                      }}
                      startIcon={<DeleteOutlineIcon aria-hidden="true" />}
                      disabled={completionData.isSaving}
                      color="inherit"
                      sx={{ color: 'text.secondary' }}
                      aria-label="Discard preferences and close the game"
                    >
                      Discard
                    </Button>
                  )}
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </Fade>
    );
  }

  // Render game round
  if (!currentRound) {
    // If there's an error, show it with retry
    if (error) {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: isMobile ? '100dvh' : 400,
            height: isMobile ? '100dvh' : 400,
            p: 3,
            boxSizing: 'border-box',
          }}
        >
          <Alert
            severity="error"
            role="alert"
            sx={{ mb: 2, maxWidth: 400 }}
          >
            {error}
          </Alert>
          <Button
            variant="contained"
            onClick={() => {
              clearError();
              isStartingRef.current = false;
              setInitState('pending');
            }}
          >
            Try Again
          </Button>
        </Box>
      );
    }

    // If we're still initializing or waiting for game to start, show loading
    // This catches the gap between init stages
    return (
      <Box
        role="status"
        aria-label="Initializing game"
        aria-busy="true"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: isMobile ? '100dvh' : 400,
          height: isMobile ? '100dvh' : 400,
          p: 3,
          boxSizing: 'border-box',
        }}
      >
        <CircularProgress size={48} aria-hidden="true" />
        <Typography variant="body1" sx={{ mt: 2 }}>
          Preparing your game...
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      ref={gameContainerRef}
      role="application"
      aria-label="This or That preference game"
      aria-describedby="game-instructions"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        // Use 100dvh for mobile to account for browser chrome, fallback to 100vh
        minHeight: isMobile ? '100dvh' : 'auto',
        height: isMobile ? '100dvh' : '100%',
        maxHeight: isMobile ? '100dvh' : 'none',
        bgcolor: 'background.default',
        position: 'relative',
        overflow: 'hidden', // Prevent any overflow
        boxSizing: 'border-box',
      }}
    >
      {/* Screen reader announcements */}
      <ScreenReaderAnnouncer message={screenReaderMessage} priority="polite" />

      {/* Header with progress - compact on mobile */}
      <Box
        component="header"
        sx={{
          p: isMobile ? 1.5 : 2,
          pt: isMobile ? 1 : 2,
          pb: isMobile ? 1 : 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Box sx={{ flex: 1 }}>
          <Typography
            variant={isMobile ? 'caption' : 'body2'}
            color="text.secondary"
            gutterBottom={!isMobile}
            sx={{ mb: isMobile ? 0.5 : undefined }}
            id="progress-label"
          >
            Question {state.roundNumber} of {state.totalRounds}
          </Typography>
          <LinearProgress
            variant="determinate"
            value={progress}
            aria-labelledby="progress-label"
            aria-valuenow={progress}
            aria-valuemin={0}
            aria-valuemax={100}
            sx={{
              height: isMobile ? 4 : 6,
              borderRadius: isMobile ? 2 : 3,
              bgcolor: alpha(theme.palette.primary.main, 0.15),
              '& .MuiLinearProgress-bar': {
                borderRadius: isMobile ? 2 : 3,
              },
            }}
          />
        </Box>
        {showCloseButton && onClose && (
          <IconButton
            onClick={onClose}
            size={isMobile ? 'small' : 'medium'}
            sx={{ ml: isMobile ? 1 : 2 }}
            aria-label="Close game and return to profile"
          >
            <CloseIcon aria-hidden="true" />
          </IconButton>
        )}
      </Box>

      {/* Prompt/Question - compact on mobile */}
      <Box sx={{ p: isMobile ? 1.5 : 2, pt: isMobile ? 1 : 2, textAlign: 'center' }}>
        <Typography
          variant={isMobile ? 'subtitle1' : 'h5'}
          component="h1"
          sx={{ fontWeight: 500, lineHeight: isMobile ? 1.3 : undefined }}
          id="current-question"
        >
          {currentRound.prompt}
        </Typography>
        {!isMobile && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 0.5 }}
            id="game-instructions"
          >
            Click to choose or use arrow keys (← for option 1, → for option 2)
          </Typography>
        )}
      </Box>

      {/* Mobile Swipe Direction Indicators */}
      {isMobile && !prefersReducedMotion && (
        <>
          {/* Left indicator (Option A) */}
          <motion.div
            style={{
              position: 'absolute',
              left: 16,
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 10,
              opacity: leftIndicatorOpacity,
              pointerEvents: 'none',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                bgcolor: alpha(theme.palette.primary.main, 0.9),
                color: 'white',
                borderRadius: 2,
                px: 1.5,
                py: 1,
                boxShadow: 3,
              }}
            >
              <ChevronLeftIcon sx={{ fontSize: 32 }} />
              <Typography variant="caption" sx={{ fontWeight: 'bold', textAlign: 'center' }}>
                {currentRound.optionA.label.substring(0, 12)}
              </Typography>
            </Box>
          </motion.div>

          {/* Right indicator (Option B) */}
          <motion.div
            style={{
              position: 'absolute',
              right: 16,
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 10,
              opacity: rightIndicatorOpacity,
              pointerEvents: 'none',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                bgcolor: alpha(theme.palette.secondary.main, 0.9),
                color: 'white',
                borderRadius: 2,
                px: 1.5,
                py: 1,
                boxShadow: 3,
              }}
            >
              <ChevronRightIcon sx={{ fontSize: 32 }} />
              <Typography variant="caption" sx={{ fontWeight: 'bold', textAlign: 'center' }}>
                {currentRound.optionB.label.substring(0, 12)}
              </Typography>
            </Box>
          </motion.div>

          {/* Swipe hint overlay */}
          {swipeDirection && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                bgcolor:
                  swipeDirection === 'left'
                    ? alpha(theme.palette.primary.main, 0.1)
                    : alpha(theme.palette.secondary.main, 0.1),
                zIndex: 5,
                pointerEvents: 'none',
                transition: 'background-color 0.2s ease',
              }}
            />
          )}
        </>
      )}

      {/* Options Container - Split Screen or Swipeable Card on Mobile */}
      {isMobile && !prefersReducedMotion ? (
        // Mobile: Swipeable stacked card layout with depth effect
        <Box
          sx={{
            flex: 1,
            position: 'relative',
            overflow: 'hidden',
            p: 1,
            minHeight: 0,
          }}
        >
          {/* Next card shadow (depth effect) - shows card "underneath" */}
          {state.roundNumber < state.totalRounds && (
            <motion.div
              style={{
                position: 'absolute',
                top: 8,
                left: 8,
                right: 8,
                bottom: 8,
                zIndex: 0,
                pointerEvents: 'none',
              }}
              variants={nextCardVariants}
              initial="hidden"
              animate="visible"
              transition={{ duration: 0.3 }}
            >
              <Box
                sx={{
                  height: '100%',
                  bgcolor: alpha(theme.palette.background.paper, 0.9),
                  borderRadius: 2,
                  boxShadow: 2,
                }}
              />
            </motion.div>
          )}

          {/* Current round card */}
          <AnimatePresence mode="wait" custom={exitDirection}>
            {!isExiting && (
            <motion.div
              key={displayRoundId}
              style={{
                x,
                rotate: cardRotation,
                scale: cardScale,
                width: '100%',
                height: '100%',
                position: 'relative',
                zIndex: 1,
              }}
              drag={!isLoading && !isAnimating && !selectedOption ? 'x' : false}
              dragConstraints={{ left: 0, right: 0 }}
              dragElastic={0.7}
              onDrag={handleDrag}
              onDragEnd={handleDragEnd}
              variants={swipeCardVariants}
              initial="enter"
              animate="center"
              exit="exit"
              custom={exitDirection}
              transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                  height: '100%',
                }}
              >
                {/* Option A */}
                <OptionCard
                  option={currentRound.optionA}
                  side="A"
                  type={currentRound.type}
                  isSelected={selectedOption === 'A'}
                  isOtherSelected={selectedOption === 'B'}
                  isDisabled={isLoading || isAnimating}
                  onSelect={() => handleSelect('A')}
                  isMobile={isMobile}
                  isTablet={isTablet}
                  imageLoaded={imagesLoaded.A}
                  onImageLoad={() => handleImageLoad('A')}
                  swipeDirection={swipeDirection}
                  isFocused={focusedOption === 'A'}
                />

                {/* Divider - "VS" indicator */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    py: 0.5,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      bgcolor: 'background.paper',
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: '50%',
                      width: 36,
                      height: 36,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 'bold',
                      color: 'text.secondary',
                    }}
                  >
                    VS
                  </Typography>
                </Box>

                {/* Option B */}
                <OptionCard
                  option={currentRound.optionB}
                  side="B"
                  type={currentRound.type}
                  isSelected={selectedOption === 'B'}
                  isOtherSelected={selectedOption === 'A'}
                  isDisabled={isLoading || isAnimating}
                  onSelect={() => handleSelect('B')}
                  isMobile={isMobile}
                  isTablet={isTablet}
                  imageLoaded={imagesLoaded.B}
                  onImageLoad={() => handleImageLoad('B')}
                  swipeDirection={swipeDirection}
                  isFocused={focusedOption === 'B'}
                />
              </Box>
            </motion.div>
            )}
          </AnimatePresence>
        </Box>
      ) : (
        // Desktop/Tablet or reduced motion: Standard side-by-side layout with animations
        <Box
          sx={{
            flex: 1,
            position: 'relative',
            p: isMobile ? 1 : 2,
            minHeight: isMobile ? 0 : 300,
          }}
        >
          {/* Next card shadow for depth effect (desktop) */}
          {!prefersReducedMotion && state.roundNumber < state.totalRounds && (
            <motion.div
              style={{
                position: 'absolute',
                top: isMobile ? 8 : 16,
                left: isMobile ? 8 : 16,
                right: isMobile ? 8 : 16,
                bottom: isMobile ? 8 : 16,
                zIndex: 0,
                pointerEvents: 'none',
              }}
              variants={nextCardVariants}
              initial="hidden"
              animate="visible"
              transition={{ duration: 0.3 }}
            >
              <Box
                sx={{
                  height: '100%',
                  bgcolor: alpha(theme.palette.background.paper, 0.8),
                  borderRadius: 2,
                  boxShadow: 1,
                }}
              />
            </motion.div>
          )}

          <AnimatePresence mode="wait" custom={exitDirection}>
            {!isExiting && (
            <motion.div
              key={displayRoundId}
              style={{
                width: '100%',
                height: '100%',
                position: 'relative',
                zIndex: 1,
              }}
              variants={swipeCardVariants}
              initial="enter"
              animate="center"
              exit="exit"
              custom={exitDirection}
              transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: isMobile ? 'column' : 'row',
                  gap: isMobile ? 1 : 2,
                  height: '100%',
                }}
              >
                {/* Option A */}
                <OptionCard
                  option={currentRound.optionA}
                  side="A"
                  type={currentRound.type}
                  isSelected={selectedOption === 'A'}
                  isOtherSelected={selectedOption === 'B'}
                  isDisabled={isLoading || isAnimating}
                  onSelect={() => handleSelect('A')}
                  isMobile={isMobile}
                  isTablet={isTablet}
                  imageLoaded={imagesLoaded.A}
                  onImageLoad={() => handleImageLoad('A')}
                  isFocused={focusedOption === 'A'}
                />

                {/* Divider - "VS" indicator */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    py: isMobile ? 0.5 : 0,
                    px: isMobile ? 0 : 1,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      bgcolor: 'background.paper',
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: '50%',
                      width: 36,
                      height: 36,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 'bold',
                      color: 'text.secondary',
                    }}
                  >
                    VS
                  </Typography>
                </Box>

                {/* Option B */}
                <OptionCard
                  option={currentRound.optionB}
                  side="B"
                  type={currentRound.type}
                  isSelected={selectedOption === 'B'}
                  isOtherSelected={selectedOption === 'A'}
                  isDisabled={isLoading || isAnimating}
                  onSelect={() => handleSelect('B')}
                  isMobile={isMobile}
                  isTablet={isTablet}
                  imageLoaded={imagesLoaded.B}
                  onImageLoad={() => handleImageLoad('B')}
                  isFocused={focusedOption === 'B'}
                />
              </Box>
            </motion.div>
            )}
          </AnimatePresence>
        </Box>
      )}

      {/* Footer with abandon option */}
      <Box
        component="footer"
        sx={{
          p: 2,
          display: 'flex',
          justifyContent: 'center',
          borderTop: 1,
          borderColor: 'divider',
        }}
      >
        <Button
          variant="text"
          size="small"
          color="inherit"
          onClick={handleAbandon}
          disabled={isLoading}
          sx={{ color: 'text.secondary' }}
          aria-label="Skip the preference game for now"
        >
          Skip for now
        </Button>
      </Box>

      {/* Error toast */}
      {error && (
        <Alert
          severity="error"
          role="alert"
          onClose={clearError}
          sx={{
            position: 'absolute',
            bottom: 70,
            left: 16,
            right: 16,
          }}
        >
          {error}
        </Alert>
      )}

      {/* Milestone celebration overlay */}
      {celebratingMilestone && (
        <MilestoneCelebration
          milestone={celebratingMilestone}
          onComplete={dismissCelebration}
        />
      )}
    </Box>
  );
};

/**
 * OptionCard - Individual option display
 */
interface OptionCardProps {
  option: GameRound['optionA'];
  side: 'A' | 'B';
  type: GameRound['type'];
  isSelected: boolean;
  isOtherSelected: boolean;
  isDisabled: boolean;
  onSelect: () => void;
  isMobile: boolean;
  isTablet: boolean;
  imageLoaded: boolean;
  onImageLoad: () => void;
  /** Current swipe direction for visual feedback (mobile only) */
  swipeDirection?: 'left' | 'right' | null;
  /** Whether this card is focused via keyboard navigation */
  isFocused?: boolean;
  /** Callback when card is hovered */
  onHover?: (isHovered: boolean) => void;
}

const OptionCard = ({
  option,
  side,
  type,
  isSelected,
  isOtherSelected,
  isDisabled,
  onSelect,
  isMobile,
  isTablet,
  imageLoaded,
  onImageLoad,
  swipeDirection,
  isFocused,
  onHover,
}: OptionCardProps) => {
  const theme = useTheme();
  const [isHovered, setIsHovered] = useState(false);

  // Handle hover state
  const handleMouseEnter = useCallback(() => {
    if (!isDisabled) {
      setIsHovered(true);
      onHover?.(true);
    }
  }, [isDisabled, onHover]);

  const handleMouseLeave = useCallback(() => {
    setIsHovered(false);
    onHover?.(false);
  }, [onHover]);

  // Determine if this card is being targeted by swipe
  const isSwipeTarget =
    (side === 'A' && swipeDirection === 'left') || (side === 'B' && swipeDirection === 'right');

  // Animation styles based on selection state, swipe, hover, and focus
  // Uses outline with negative offset for internal frame highlight
  const getAnimationStyles = () => {
    if (isSelected) {
      return {
        transform: 'scale(1.02)',
        // Internal frame highlight using outline with negative offset
        outline: `4px solid ${theme.palette.primary.main}`,
        outlineOffset: '-4px',
      };
    }
    if (isOtherSelected) {
      return {
        transform: 'scale(0.95)',
      };
    }
    // Highlight card being targeted by swipe (internal frame)
    if (isSwipeTarget) {
      return {
        transform: 'scale(1.02)',
        outline: side === 'A'
          ? `4px solid ${theme.palette.primary.main}`
          : `4px solid ${theme.palette.secondary.main}`,
        outlineOffset: '-4px',
      };
    }
    // Keyboard focus state (desktop) - internal frame
    if (isFocused && !isMobile) {
      return {
        transform: 'scale(1.03) translateY(-4px) rotate(1deg)',
        outline: `3px solid ${theme.palette.primary.main}`,
        outlineOffset: '-3px',
        boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.25)}`,
      };
    }
    return {};
  };

  // Enhanced hover styles for desktop (lift + slight rotation effect)
  const getHoverStyles = () => {
    if (isMobile || isDisabled || isSelected || isOtherSelected) {
      return {};
    }
    return {
      '&:hover': {
        transform: 'scale(1.02) translateY(-6px) rotate(0.5deg)',
        boxShadow: `0 12px 28px ${alpha(theme.palette.common.black, 0.15)}, 0 4px 10px ${alpha(theme.palette.common.black, 0.1)}`,
      },
    };
  };

  // Accessible label for the option
  const accessibleLabel = `Option ${side === 'A' ? '1' : '2'}: ${option.label}${option.description ? `. ${option.description}` : ''}${isSelected ? '. Selected' : ''}`;
  const keyboardHint = side === 'A' ? 'Press left arrow or 1 to select' : 'Press right arrow or 2 to select';

  // Render image-based option
  if (type === 'image' && option.imageUrl) {
    return (
      <Paper
        elevation={isSelected ? 8 : isHovered ? 6 : 2}
        role="button"
        tabIndex={isDisabled ? -1 : 0}
        aria-label={accessibleLabel}
        aria-pressed={isSelected}
        aria-disabled={isDisabled}
        aria-describedby={!isMobile ? `keyboard-hint-${side}` : undefined}
        onKeyDown={(e) => {
          if (!isDisabled && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            onSelect();
          }
        }}
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          borderRadius: 2,
          cursor: isDisabled ? 'default' : 'pointer',
          // Specific transitions for smooth animations
          transition: 'transform 0.2s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.2s cubic-bezier(0.4, 0, 0.2, 1), outline 0.15s ease-out',
          position: 'relative',
          minHeight: isMobile ? 180 : 250,
          '&:focus-visible': {
            // Internal frame for focus state
            outline: `3px solid ${theme.palette.primary.main}`,
            outlineOffset: '-3px',
          },
          ...getHoverStyles(),
          ...getAnimationStyles(),
        }}
        onClick={() => !isDisabled && onSelect()}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {/* Hidden keyboard hint for screen readers */}
        {!isMobile && (
          <Box component="span" id={`keyboard-hint-${side}`} sx={visuallyHidden}>{keyboardHint}</Box>
        )}
        {/* Image skeleton while loading */}
        {!imageLoaded && (
          <Skeleton
            variant="rectangular"
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              height: '100%',
            }}
          />
        )}

        {/* Image */}
        <Box
          sx={{
            flex: 1,
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <img
            src={option.imageUrl}
            alt={option.imageAlt || option.label}
            onLoad={onImageLoad}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: imageLoaded ? 'block' : 'none',
            }}
          />

          {/* Gradient overlay for text readability */}
          <Box
            sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              background: 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, transparent 100%)',
              p: 2,
              pt: 4,
            }}
          >
            <Typography
              variant={isMobile ? 'body1' : 'h6'}
              sx={{
                color: 'white',
                fontWeight: 600,
                textShadow: '0 1px 3px rgba(0,0,0,0.3)',
              }}
            >
              {option.label}
            </Typography>
            {option.description && (
              <Typography
                variant="body2"
                sx={{
                  color: 'rgba(255,255,255,0.85)',
                  mt: 0.5,
                  display: isMobile ? 'none' : 'block',
                }}
              >
                {option.description}
              </Typography>
            )}
          </Box>
        </Box>

        {/* Selection indicator */}
        {isSelected && (
          <Zoom in>
            <Box
              aria-hidden="true"
              sx={{
                position: 'absolute',
                top: 12,
                right: 12,
                bgcolor: 'primary.main',
                borderRadius: '50%',
                p: 0.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <CheckCircleIcon sx={{ color: 'white', fontSize: 28 }} />
            </Box>
          </Zoom>
        )}
      </Paper>
    );
  }

  // Render text-based option
  return (
    <Paper
      elevation={isSelected ? 8 : isHovered ? 6 : 2}
      role="button"
      tabIndex={isDisabled ? -1 : 0}
      aria-label={accessibleLabel}
      aria-pressed={isSelected}
      aria-disabled={isDisabled}
      aria-describedby={!isMobile ? `keyboard-hint-text-${side}` : undefined}
      onKeyDown={(e) => {
        if (!isDisabled && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          onSelect();
        }
      }}
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        p: isMobile ? 3 : 4,
        borderRadius: 2,
        cursor: isDisabled ? 'default' : 'pointer',
        // Specific transitions for smooth animations
        transition: 'transform 0.2s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.2s cubic-bezier(0.4, 0, 0.2, 1), outline 0.15s ease-out, background-color 0.2s ease',
        position: 'relative',
        minHeight: isMobile ? 120 : isTablet ? 150 : 200,
        bgcolor: isHovered && !isMobile && !isSelected
            ? alpha(theme.palette.primary.main, 0.05)
            : 'background.paper',
        '&:focus-visible': {
          // Internal frame for focus state
          outline: `3px solid ${theme.palette.primary.main}`,
          outlineOffset: '-3px',
        },
        ...getHoverStyles(),
        ...getAnimationStyles(),
      }}
      onClick={() => !isDisabled && onSelect()}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Hidden keyboard hint for screen readers */}
      {!isMobile && (
        <Box component="span" id={`keyboard-hint-text-${side}`} sx={visuallyHidden}>{keyboardHint}</Box>
      )}
      <Typography
        variant={isMobile ? 'h5' : 'h4'}
        component="span"
        sx={{
          fontWeight: 600,
          textAlign: 'center',
          color: isSelected ? 'primary.main' : 'text.primary',
        }}
      >
        {option.label}
      </Typography>

      {option.description && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            mt: 1,
            textAlign: 'center',
            maxWidth: '80%',
          }}
        >
          {option.description}
        </Typography>
      )}

      {/* Selection indicator */}
      {isSelected && (
        <Zoom in>
          <Box
            aria-hidden="true"
            sx={{
              position: 'absolute',
              top: 12,
              right: 12,
              bgcolor: 'primary.main',
              borderRadius: '50%',
              p: 0.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CheckCircleIcon sx={{ color: 'white', fontSize: 24 }} />
          </Box>
        </Zoom>
      )}
    </Paper>
  );
};

export default ThisOrThatGame;
